import { DatePicker } from '@components/common';
import { useAppSelector } from '@hooks/useRedux';
import { selectAccommodationProperty } from '@store/accommodation';
import {
  useBookingSources,
  useCountries,
  useCreateBooking,
  useRoomTypes,
  useRooms,
} from '@hooks/accommodation';
import { selectCompanyId } from '@store/auth/authSelectors';
import { ArrowLeft, Check, Search, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as accommodationService from '../../../../services/accommodationService';
import {
  AccommodationClient,
  AccommodationRoom,
} from '../../../../types/accommodation';
import { apiError } from '../../../../util/notify';

// ----------------------------------------------------------------
// Types local to this form
// ----------------------------------------------------------------
interface SelectedRoom {
  room: AccommodationRoom;
  price_per_night: number;
}

interface FormDates {
  check_in: string;
  check_out: string;
  adults: number;
  children: number;
  notes: string;
}

const STEPS = ['Dates', 'Rooms', 'Guest', 'Confirm'];

function nightsBetween(checkIn: string, checkOut: string) {
  const ms = new Date(checkOut).getTime() - new Date(checkIn).getTime();
  return Math.round(ms / 86_400_000);
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-gray-50 pb-2">
      <span className="text-gray-500">{label}</span>
      <span className="text-right font-medium text-gray-900">{value}</span>
    </div>
  );
}

// ----------------------------------------------------------------
export const NewBookingPage: React.FC = () => {
  const navigate = useNavigate();
  const property = useAppSelector(selectAccommodationProperty);
  const companyId = useAppSelector(selectCompanyId);
  const { data: rawRooms = [] } = useRooms(property?.id);
  const allRooms = rawRooms.filter((r) => r.is_active);
  const { data: allRoomTypes = [] } = useRoomTypes(property?.id);
  const roomTypes = allRoomTypes.filter((rt) => rt.is_active);
  const { data: bookingSources = [] } = useBookingSources();
  const { data: countries = [] } = useCountries();
  const createBooking = useCreateBooking();
  const isSubmitting = createBooking.isPending;

  const [step, setStep] = useState(0);

  // Step 1 – dates
  const [dates, setDates] = useState<FormDates>({
    check_in: today(),
    check_out: addDays(today(), 1),
    adults: 1,
    children: 0,
    notes: '',
  });

  // Step 2 – rooms
  const [bookedIds, setBookedIds] = useState<Set<string>>(new Set());
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [selectedRooms, setSelectedRooms] = useState<SelectedRoom[]>([]);

  // Step 3 – guest
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState<AccommodationClient[]>([]);
  const [searchingClients, setSearchingClients] = useState(false);
  const [selectedClient, setSelectedClient] =
    useState<AccommodationClient | null>(null);
  const [newGuest, setNewGuest] = useState({
    first_name: '',
    last_name: '',
    email: '',
    contact: '',
    id_type: '',
    id_number: '',
  });
  const [guestMode, setGuestMode] = useState<'search' | 'new'>('search');
  const [selectedSourceId, setSelectedSourceId] = useState<number | null>(null);
  const [guestCountryId, setGuestCountryId] = useState<number | null>(null);
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const countryRef = useRef<HTMLDivElement>(null);

  // ----------------------------------------------------------------
  // Close country dropdown on outside click
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!showCountryDropdown) return undefined;
    const handler = (e: MouseEvent) => {
      if (
        countryRef.current &&
        !countryRef.current.contains(e.target as Node)
      ) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showCountryDropdown]);

  // ----------------------------------------------------------------
  // Fetch availability when moving to step 2
  // ----------------------------------------------------------------
  const fetchAvailability = useCallback(async () => {
    if (!property) return;
    setLoadingAvailability(true);
    try {
      const ids = await accommodationService.fetchBookedRoomIds(
        property.id,
        dates.check_in,
        dates.check_out,
      );
      setBookedIds(new Set(ids));
    } catch {
      // non-blocking; user can still see rooms
    } finally {
      setLoadingAvailability(false);
    }
  }, [property, dates.check_in, dates.check_out]);

  // ----------------------------------------------------------------
  // Client search
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!clientSearch.trim() || clientSearch.length < 2 || !companyId) {
      setClientResults([]);
      return undefined;
    }
    const id = setTimeout(async () => {
      setSearchingClients(true);
      try {
        const results = await accommodationService.searchClients(
          companyId,
          clientSearch,
        );
        setClientResults(results);
      } finally {
        setSearchingClients(false);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [clientSearch, companyId]);

  // ----------------------------------------------------------------
  // Step validation
  // ----------------------------------------------------------------
  const validateStep = (): boolean => {
    if (step === 0) {
      if (!dates.check_in) {
        apiError('Please select a check-in date.');
        return false;
      }
      if (!dates.check_out) {
        apiError('Please select a check-out date.');
        return false;
      }
      if (dates.check_in < today()) {
        apiError('Check-in date cannot be in the past.');
        return false;
      }
      if (nightsBetween(dates.check_in, dates.check_out) < 1) {
        apiError('Check-out must be at least one day after check-in.');
        return false;
      }
      return true;
    }

    if (step === 1) {
      if (selectedRooms.length === 0) {
        apiError('Please select at least one room.');
        return false;
      }
      return true;
    }

    if (step === 2) {
      if (guestMode === 'search') {
        if (!selectedClient) {
          apiError('Please select a guest or switch to "New guest".');
          return false;
        }
      } else {
        if (!newGuest.first_name.trim()) {
          apiError('Guest first name is required.');
          return false;
        }
        if (!newGuest.last_name.trim()) {
          apiError('Guest last name is required.');
          return false;
        }
      }
      return true;
    }

    return true;
  };

  // ----------------------------------------------------------------
  // Step navigation
  // ----------------------------------------------------------------
  const goNext = async () => {
    if (!validateStep()) return;
    if (step === 0) {
      await fetchAvailability();
    }
    setStep((s) => s + 1);
  };

  const goBack = () => setStep((s) => s - 1);

  // ----------------------------------------------------------------
  // Toggle room selection
  // ----------------------------------------------------------------
  const toggleRoom = (room: AccommodationRoom) => {
    setSelectedRooms((prev) => {
      const exists = prev.find((r) => r.room.id === room.id);
      if (exists) return prev.filter((r) => r.room.id !== room.id);
      const basePrice = room.room_type?.base_price ?? 0;
      return [...prev, { room, price_per_night: basePrice }];
    });
  };

  const updateRoomPrice = (roomId: string, price: number) => {
    setSelectedRooms((prev) =>
      prev.map((sr) =>
        sr.room.id === roomId ? { ...sr, price_per_night: price } : sr,
      ),
    );
  };

  // ----------------------------------------------------------------
  // Submit
  // ----------------------------------------------------------------
  const handleSubmit = async () => {
    if (!property) return;
    if (selectedRooms.length === 0) {
      apiError('Please select at least one room.');
      return;
    }

    let clientId: string;

    if (guestMode === 'search') {
      if (!selectedClient) {
        apiError('Please select a guest.');
        return;
      }
      clientId = selectedClient.id;
    } else {
      if (!newGuest.first_name.trim() || !newGuest.last_name.trim()) {
        apiError('Guest first and last name are required.');
        return;
      }
      if (!companyId) {
        apiError('Company not found.');
        return;
      }

      if (guestCountryId === null) {
        apiError('Please select a country for the guest.');
        return;
      }

      if (guestCountryId === null) {
        apiError('Please select a country for the guest.');
        return;
      }

      try {
        const created = await accommodationService.createClient(companyId, {
          first_name: newGuest.first_name.trim(),
          last_name: newGuest.last_name.trim(),
          email: newGuest.email.trim() || null,
          contact: newGuest.contact.trim() || null,
          address: null,
          tin_number: null,
          country_id: guestCountryId,
          date_of_birth: null,
          id_type: newGuest.id_type.trim() || null,
          id_number: newGuest.id_number.trim() || null,
          accommodation_notes: null,
          nationality: '',
        });
        clientId = created.id;
      } catch (err) {
        apiError(err instanceof Error ? err.message : 'Failed to create guest');
        return;
      }
    }

    const nights = nightsBetween(dates.check_in, dates.check_out);
    const rooms = selectedRooms.map((sr) => ({
      room_id: sr.room.id,
      room_type_id: sr.room.room_type_id,
      price_per_night: sr.price_per_night,
      nights,
      subtotal: sr.price_per_night * nights,
    }));
    const total = rooms.reduce((sum, r) => sum + r.subtotal, 0);

    createBooking.mutate(
      {
        property_id: property.id,
        client_id: clientId,
        check_in: dates.check_in,
        check_out: dates.check_out,
        adults: dates.adults,
        children: dates.children,
        total_amount: total,
        currency: property.currency,
        notes: dates.notes.trim() || null,
        rooms,
        source_id: selectedSourceId ?? undefined,
        guest_country_id: guestCountryId ?? undefined,
      },
      {
        onSuccess: (booking) =>
          navigate(`/accommodation/bookings/${booking.id}`),
      },
    );
  };

  if (!property) return null;

  const nights = nightsBetween(dates.check_in, dates.check_out);
  const totalAmount = selectedRooms.reduce(
    (sum, sr) => sum + sr.price_per_night * Math.max(nights, 1),
    0,
  );

  // min check-out = day after check-in
  const minCheckOut = addDays(dates.check_in, 1);

  // ----------------------------------------------------------------
  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Back + title */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/accommodation/bookings')}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"
        >
          <ArrowLeft size={16} />
        </button>
        <h3 className="text-base font-semibold text-gray-900">New booking</h3>
      </div>

      {/* Step indicators */}
      <div className="flex items-center gap-1">
        {STEPS.map((label, i) => (
          <React.Fragment key={label}>
            <div className="flex items-center gap-1.5">
              <div
                className={[
                  'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold',
                  i <= step
                    ? 'bg-pos-primary text-white'
                    : 'bg-gray-100 text-gray-400',
                ].join(' ')}
              >
                {i < step ? <Check size={12} /> : i + 1}
              </div>
              <span
                className={[
                  'text-xs font-medium',
                  i === step ? 'text-gray-900' : 'text-gray-400',
                ].join(' ')}
              >
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className="mx-2 h-px flex-1 bg-gray-200" />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* ---- Step 0: Dates & guests ---- */}
      {step === 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white p-6">
          <h4 className="mb-4 text-sm font-semibold text-gray-900">
            Dates & guests
          </h4>
          <div className="space-y-4">
            {/* Check-in date */}
            <DatePicker
              mode="single"
              id="nb-check-in"
              label="Check-in *"
              value={dates.check_in}
              minDate={today()}
              placeholder="Select check-in date"
              onChange={(iso) => {
                // If new check-in is same/after current check-out, push check-out forward
                const newCheckOut =
                  dates.check_out <= iso ? addDays(iso, 1) : dates.check_out;
                setDates({ ...dates, check_in: iso, check_out: newCheckOut });
              }}
            />

            {/* Check-out date */}
            <DatePicker
              mode="single"
              id="nb-check-out"
              label="Check-out *"
              value={dates.check_out}
              minDate={minCheckOut}
              placeholder="Select check-out date"
              onChange={(iso) => setDates({ ...dates, check_out: iso })}
            />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="nb-adults"
                  className="mb-1 block text-xs font-medium text-gray-700"
                >
                  Adults
                </label>
                <input
                  id="nb-adults"
                  type="number"
                  min={1}
                  value={dates.adults}
                  onChange={(e) =>
                    setDates({ ...dates, adults: Number(e.target.value) })
                  }
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                />
              </div>
              <div>
                <label
                  htmlFor="nb-children"
                  className="mb-1 block text-xs font-medium text-gray-700"
                >
                  Children
                </label>
                <input
                  id="nb-children"
                  type="number"
                  min={0}
                  value={dates.children}
                  onChange={(e) =>
                    setDates({ ...dates, children: Number(e.target.value) })
                  }
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="nb-notes"
                className="mb-1 block text-xs font-medium text-gray-700"
              >
                Notes
              </label>
              <textarea
                id="nb-notes"
                rows={2}
                value={dates.notes}
                onChange={(e) => setDates({ ...dates, notes: e.target.value })}
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                placeholder="Special requests…"
              />
            </div>

            {/* Booking source */}
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="mb-1 block text-xs font-medium text-gray-700">
                How did this booking come to you?
              </label>
              <div className="flex flex-wrap gap-2">
                {bookingSources.map((source) => (
                  <button
                    key={source.id}
                    type="button"
                    onClick={() =>
                      setSelectedSourceId(
                        source.id === selectedSourceId ? null : source.id,
                      )
                    }
                    className={[
                      'rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
                      selectedSourceId === source.id
                        ? 'border-pos-primary bg-pos-primary/10 text-pos-primary'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300',
                    ].join(' ')}
                  >
                    {source.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={goNext}
              className="rounded-xl bg-pos-primary px-5 py-2 text-sm font-medium text-white hover:bg-pos-primary/90"
            >
              Next: Select rooms
            </button>
          </div>
        </div>
      )}

      {/* ---- Step 1: Room selection ---- */}
      {step === 1 && (
        <div className="rounded-2xl border border-gray-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-900">
              Select rooms
            </h4>
            <span className="text-xs text-gray-400">
              {nights} night{nights !== 1 ? 's' : ''} ·{' '}
              {loadingAvailability ? 'Checking availability…' : ''}
            </span>
          </div>

          {allRooms.length === 0 ? (
            <p className="text-sm text-gray-400">
              No rooms available in the selected period. Please go back and
              adjust the dates. Or Make sure you have selected an outlet.
            </p>
          ) : (
            <div className="space-y-4">
              {roomTypes.map((rt) => {
                const rooms = allRooms.filter((r) => r.room_type_id === rt.id);
                if (rooms.length === 0) return null;
                return (
                  <div key={rt.id}>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {rt.name} — {property.currency}{' '}
                      {rt.base_price.toLocaleString()} / night
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {rooms.map((room) => {
                        const booked = bookedIds.has(room.id);
                        const selected = selectedRooms.some(
                          (sr) => sr.room.id === room.id,
                        );
                        return (
                          <button
                            key={room.id}
                            type="button"
                            disabled={booked}
                            onClick={() => !booked && toggleRoom(room)}
                            className={[
                              'flex items-center justify-between rounded-xl border px-3 py-2.5 text-left transition-colors',
                              booked
                                ? 'cursor-not-allowed border-gray-100 opacity-40'
                                : selected
                                  ? 'border-pos-primary bg-pos-primary/5'
                                  : 'border-gray-200 hover:border-pos-primary/40',
                            ].join(' ')}
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                Room {room.room_number}
                              </p>
                              {room.floor && (
                                <p className="text-xs text-gray-400">
                                  Floor {room.floor}
                                </p>
                              )}
                            </div>
                            {booked ? (
                              <span className="text-xs text-gray-400">
                                Booked
                              </span>
                            ) : selected ? (
                              <Check size={14} className="text-pos-primary" />
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {selectedRooms.length > 0 && (
            <div className="mt-4 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Rate per night
              </p>
              {selectedRooms.map((sr) => (
                <div
                  key={sr.room.id}
                  className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5"
                >
                  <span className="flex-1 text-sm font-medium text-gray-800">
                    Room {sr.room.room_number}
                  </span>
                  <span className="text-xs text-gray-400">
                    {property.currency}
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={sr.price_per_night}
                    onChange={(e) =>
                      updateRoomPrice(
                        sr.room.id,
                        Math.max(0, Number(e.target.value)),
                      )
                    }
                    className="w-32 rounded-lg border border-gray-300 px-2 py-1 text-right text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                  />
                </div>
              ))}
              <div className="rounded-xl bg-pos-primary/5 px-4 py-2.5 text-sm">
                <span className="font-medium text-gray-900">
                  {selectedRooms.length} room
                  {selectedRooms.length !== 1 ? 's' : ''}
                </span>
                <span className="text-gray-500">
                  {' '}
                  · {property.currency} {totalAmount.toLocaleString()} total (
                  {nights} night{nights !== 1 ? 's' : ''})
                </span>
              </div>
            </div>
          )}

          <div className="mt-5 flex justify-between">
            <button
              type="button"
              onClick={goBack}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={selectedRooms.length === 0}
              className="rounded-xl bg-pos-primary px-5 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
            >
              Next: Guest details
            </button>
          </div>
        </div>
      )}

      {/* ---- Step 2: Guest ---- */}
      {step === 2 && (
        <div className="rounded-2xl border border-gray-200 bg-white p-6">
          <h4 className="mb-4 text-sm font-semibold text-gray-900">
            Guest details
          </h4>

          <div className="mb-4 flex gap-2">
            <button
              type="button"
              onClick={() => setGuestMode('search')}
              className={[
                'rounded-lg px-3 py-1.5 text-xs font-medium',
                guestMode === 'search'
                  ? 'bg-pos-primary text-white'
                  : 'border border-gray-200 text-gray-600',
              ].join(' ')}
            >
              Search existing guest
            </button>
            <button
              type="button"
              onClick={() => setGuestMode('new')}
              className={[
                'rounded-lg px-3 py-1.5 text-xs font-medium',
                guestMode === 'new'
                  ? 'bg-pos-primary text-white'
                  : 'border border-gray-200 text-gray-600',
              ].join(' ')}
            >
              New guest
            </button>
          </div>

          {guestMode === 'search' && (
            <div className="space-y-3">
              <div className="relative">
                <Search
                  size={13}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Search by name, email, or phone…"
                  className="w-full rounded-xl border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                />
                {searchingClients && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                    Searching…
                  </span>
                )}
              </div>

              {selectedClient && (
                <div className="flex items-center justify-between rounded-xl border border-pos-primary bg-pos-primary/5 px-3 py-2.5">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedClient.first_name} {selectedClient.last_name}
                    </p>
                    <p className="text-xs text-gray-400">
                      {selectedClient.email ?? selectedClient.contact}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedClient(null)}
                    className="rounded p-0.5 text-gray-400 hover:bg-gray-100"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

              {!selectedClient && clientResults.length > 0 && (
                <div className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200">
                  {clientResults.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        setSelectedClient(c);
                        setClientSearch('');
                        setClientResults([]);
                      }}
                      className="flex w-full items-start px-3 py-2.5 text-left hover:bg-gray-50"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {c.first_name} {c.last_name}
                        </p>
                        <p className="text-xs text-gray-400">
                          {c.email ?? c.contact ?? '—'}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {guestMode === 'new' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="nb-first-name"
                    className="mb-1 block text-xs font-medium text-gray-700"
                  >
                    First name *
                  </label>
                  <input
                    id="nb-first-name"
                    value={newGuest.first_name}
                    onChange={(e) =>
                      setNewGuest({ ...newGuest, first_name: e.target.value })
                    }
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                  />
                </div>
                <div>
                  <label
                    htmlFor="nb-last-name"
                    className="mb-1 block text-xs font-medium text-gray-700"
                  >
                    Last name *
                  </label>
                  <input
                    id="nb-last-name"
                    value={newGuest.last_name}
                    onChange={(e) =>
                      setNewGuest({ ...newGuest, last_name: e.target.value })
                    }
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="nb-email"
                    className="mb-1 block text-xs font-medium text-gray-700"
                  >
                    Email
                  </label>
                  <input
                    id="nb-email"
                    type="email"
                    value={newGuest.email}
                    onChange={(e) =>
                      setNewGuest({ ...newGuest, email: e.target.value })
                    }
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                  />
                </div>
                <div>
                  <label
                    htmlFor="nb-phone"
                    className="mb-1 block text-xs font-medium text-gray-700"
                  >
                    Phone
                  </label>
                  <input
                    id="nb-phone"
                    value={newGuest.contact}
                    onChange={(e) =>
                      setNewGuest({ ...newGuest, contact: e.target.value })
                    }
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {/* Country searchable dropdown */}
                <div ref={countryRef}>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    Nationality / Country
                  </label>
                  <div className="relative">
                    <input
                      value={countrySearch}
                      onChange={(e) => {
                        setCountrySearch(e.target.value);
                        setShowCountryDropdown(true);
                        if (!e.target.value) setGuestCountryId(null);
                      }}
                      onFocus={() => setShowCountryDropdown(true)}
                      placeholder={
                        guestCountryId
                          ? (countries.find((c) => c.id === guestCountryId)
                              ?.name ?? 'Search country…')
                          : 'Search country…'
                      }
                      className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                    />
                    {showCountryDropdown && countrySearch.trim().length > 0 && (
                      <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg">
                        {countries
                          .filter((c) =>
                            c.name
                              .toLowerCase()
                              .includes(countrySearch.toLowerCase()),
                          )
                          .slice(0, 20)
                          .map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => {
                                setGuestCountryId(c.id);
                                setCountrySearch(c.name);
                                setShowCountryDropdown(false);
                              }}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                            >
                              <span className="text-xs text-gray-400">
                                {c.iso_code}
                              </span>
                              {c.name}
                            </button>
                          ))}
                        {countries.filter((c) =>
                          c.name
                            .toLowerCase()
                            .includes(countrySearch.toLowerCase()),
                        ).length === 0 && (
                          <p className="px-3 py-2 text-sm text-gray-400">
                            No country found
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="nb-id-type"
                    className="mb-1 block text-xs font-medium text-gray-700"
                  >
                    ID type
                  </label>
                  <input
                    id="nb-id-type"
                    value={newGuest.id_type}
                    onChange={(e) =>
                      setNewGuest({ ...newGuest, id_type: e.target.value })
                    }
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                    placeholder="e.g. Passport"
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="nb-id-number"
                  className="mb-1 block text-xs font-medium text-gray-700"
                >
                  ID number
                </label>
                <input
                  id="nb-id-number"
                  value={newGuest.id_number}
                  onChange={(e) =>
                    setNewGuest({ ...newGuest, id_number: e.target.value })
                  }
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                />
              </div>
            </div>
          )}

          <div className="mt-5 flex justify-between">
            <button
              type="button"
              onClick={goBack}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="button"
              onClick={goNext}
              className="rounded-xl bg-pos-primary px-5 py-2 text-sm font-medium text-white hover:bg-pos-primary/90"
            >
              Review booking
            </button>
          </div>
        </div>
      )}

      {/* ---- Step 3: Confirm ---- */}
      {step === 3 && (
        <div className="rounded-2xl border border-gray-200 bg-white p-6">
          <h4 className="mb-4 text-sm font-semibold text-gray-900">
            Confirm booking
          </h4>

          <div className="space-y-3 text-sm">
            <SummaryRow
              label="Guest"
              value={
                selectedClient
                  ? `${selectedClient.first_name} ${selectedClient.last_name}`
                  : `${newGuest.first_name} ${newGuest.last_name} (new)`
              }
            />
            <SummaryRow
              label="Check-in"
              value={new Date(`${dates.check_in}T00:00:00`).toLocaleDateString(
                undefined,
                { day: 'numeric', month: 'short', year: 'numeric' },
              )}
            />
            <SummaryRow
              label="Check-out"
              value={new Date(`${dates.check_out}T00:00:00`).toLocaleDateString(
                undefined,
                {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                },
              )}
            />
            <SummaryRow label="Nights" value={String(nights)} />
            <SummaryRow
              label="Adults / Children"
              value={`${dates.adults} / ${dates.children}`}
            />
            {selectedRooms.map((sr) => (
              <SummaryRow
                key={sr.room.id}
                label={`Room ${sr.room.room_number}`}
                value={`${property.currency} ${sr.price_per_night.toLocaleString()} / night`}
              />
            ))}
            <SummaryRow
              label="Total"
              value={`${property.currency} ${totalAmount.toLocaleString()}`}
            />
            {dates.notes && <SummaryRow label="Notes" value={dates.notes} />}
            {selectedSourceId && (
              <SummaryRow
                label="Booking source"
                value={
                  bookingSources.find((s) => s.id === selectedSourceId)?.name ??
                  '—'
                }
              />
            )}
            {guestCountryId && (
              <SummaryRow
                label="Guest country"
                value={
                  countries.find((c) => c.id === guestCountryId)?.name ?? '—'
                }
              />
            )}
          </div>

          <div className="mt-5 flex justify-between">
            <button
              type="button"
              onClick={goBack}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={handleSubmit}
              className="rounded-xl bg-pos-primary px-5 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
            >
              {isSubmitting ? 'Creating…' : 'Confirm & create'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
